import { matchBreakpoint } from '@chh/ui/js/matchBreakpoint';

class InteractiveSvg extends HTMLElement {
  connectedCallback() {
    this._buttonsGroup = this.querySelector('[id*="buttons"]');
    this._popoversGroup = this.querySelector('[id*="popovers"]');
    this._buttonNodes = this._buttonsGroup?.children ?? [];
    this._popoverNodes = this._popoversGroup?.children ?? [];
    this._activeNodes = this.querySelector('[id*="active"]')?.children ?? [];
    this._outsideNode = this.querySelector('[id*="outside"]')?.children[0];

    this._controller = new AbortController();
    const { signal } = this._controller;

    this._mql = matchBreakpoint('desktop');
    this._mql.addEventListener('change', this._onBreakpointChange, { signal });
    this._onBreakpointChange(this._mql);

    this.addEventListener('click', this._onClick, { signal });
  }

  disconnectedCallback() {
    this._controller?.abort();
  }

  _onClick = (e) => {
    if (this._outsideNode?.contains(e.target)) {
      this._handleOutsideClick();
      return;
    }

    if (e.target.closest('[id*="close"]')) {
      this._handleOutsideClick();
      return;
    }

    const clickedButton = this._childOf(this._buttonsGroup, e.target);
    if (clickedButton) {
      this._handleClickTrigger(clickedButton, e);
      return;
    }

    const clickedPopover = this._childOf(this._popoversGroup, e.target);
    if (clickedPopover) {
      this._handleClickTrigger(clickedPopover, e);
    }
  };

  _childOf(group, target) {
    let node = target;
    while (node && node !== group) {
      if (node.parentElement === group) return node;
      node = node.parentElement;
    }
    return null;
  }

  _onBreakpointChange = (e) => {
    if (!e.matches && e.type === 'change') {
      for (const item of this._buttonNodes) {
        item.removeAttribute('tabindex');
        item.removeAttribute('href');
      }
      for (const item of this._popoverNodes) {
        item.removeAttribute('aria-labelledby');
      }
      this._handleOutsideClick();
    } else {
      for (const item of this._buttonNodes) {
        const id = this._getItemId(item.id);
        item.setAttribute('tabindex', '0');
        item.setAttribute('href', `#${id}-pop a`);
      }
      for (const item of this._popoverNodes) {
        const id = this._getItemId(item.id);
        item.setAttribute('aria-labelledby', `${id}-button`);
      }
    }
  };

  _handleClickTrigger(triggerNode, e) {
    const id = this._getItemId(triggerNode.id);
    const isKeyboard = e.screenX === 0 && e.screenY === 0;

    const buttonNode = this.querySelector(`#${id}-button`);
    const activeNode = this.querySelector(`#${id}-active`);
    const popoverNode = this.querySelector(`#${id}-pop`);
    const open = buttonNode.getAttribute('aria-expanded') === 'true';

    if (open) {
      this._animateClose(buttonNode, activeNode, popoverNode);
    } else {
      this._animateOpen(buttonNode, activeNode, popoverNode, isKeyboard);
    }
  }

  _handleOutsideClick() {
    for (const item of this._buttonNodes) {
      item.setAttribute('aria-expanded', 'false');
    }
    for (const item of this._popoverNodes) {
      item.style.visibility = 'hidden';
    }
    for (const item of this._activeNodes) {
      item.style.visibility = 'hidden';
    }
    if (this._outsideNode) {
      this._outsideNode.style.pointerEvents = 'none';
      this._outsideNode.style.opacity = '0';
    }
  }

  _getItemId(hash) {
    return hash.split('-').slice(0, -1).join('-');
  }

  _animateClose(triggerNode, activeNode, popoverNode) {
    triggerNode.setAttribute('aria-expanded', 'false');
    activeNode.style.visibility = 'hidden';
    popoverNode.style.visibility = 'hidden';

    popoverNode.querySelector('[id*="close"]').removeAttribute('tabindex');

    this._outsideNode.style.pointerEvents = 'none';
    this._outsideNode.style.opacity = '0';
  }

  _animateOpen(triggerNode, activeNode, popoverNode, isKeyboard) {
    const id = this._getItemId(triggerNode.id);
    triggerNode.setAttribute('aria-expanded', 'true');

    const closeNode = popoverNode.querySelector('[id*="close"]');
    closeNode.setAttribute('tabindex', '0');
    closeNode.setAttribute('aria-label', 'close popover');

    if (isKeyboard) {
      closeNode.setAttribute('href', `#${id}-button`);
      closeNode.focus();
    }

    activeNode.style.visibility = 'visible';
    popoverNode.style.visibility = 'visible';

    this._outsideNode.style.pointerEvents = 'auto';
    this._outsideNode.style.opacity = '.5';
  }
}

customElements.define('chh-interactive-svg', InteractiveSvg);

export default InteractiveSvg;
